// The workspace's place: the same frame as Me (rail, sidebar, panel) with the
// Workspace sidebar, or the settings sidebar (both fold to their icons); the
// Workspace sidebar goes around Me's pages opened from it too. While the workspace is being
// loaded the frame is drawn empty; a workspace one is not in (or that is not
// there) is told in the panel, the same for both.
import { useQuery } from "@tanstack/react-query";
import { Navigate, Outlet } from "@tanstack/react-router";
import { m } from "@trameria/cloud-i18n";
import {
  ApiError,
  apiErrorMessage,
  Frame,
  type MeSidebarSwap,
  PanelBody,
  PanelHeader,
  SettingsDockUp,
  useApi,
  useMeWorkspace,
} from "@trameria/ui-core";
import { type ReactNode, useLayoutEffect } from "react";
import {
  CurrentWorkspaceContext,
  useWorkspace,
  useWorkspaceHost,
  type WorkspaceHost,
  WorkspaceHostContext,
} from "./context";
import { useMePlace } from "./me-place";
import { workspaceViewQuery } from "./queries";
import { sectionShown } from "./sections";
import { SettingsSidebar } from "./settings/sidebar";
import { WorkspaceSidebar, WorkspaceSidebarCollapsed } from "./sidebar";
import { useWorkspaceSpaces } from "./space-groups";

const EmptySidebar = () => <div className="h-full bg-sidebar" />;

export function WorkspaceRoot({
  host,
  slug,
}: {
  host: WorkspaceHost;
  slug: string;
}) {
  const view = useQuery(workspaceViewQuery(useApi(), slug));
  if (!view.data)
    return (
      // Folded too when that is kept, so the sidebar keeps its width when the workspace arrives.
      <Frame sidebar={<EmptySidebar />} collapsed={<EmptySidebar />}>
        {view.isError && (
          <>
            <PanelHeader title={m.ws_missing_title()} />
            <PanelBody>
              <p className="text-[13px] text-muted">
                {view.error instanceof ApiError && view.error.status === 404
                  ? m.ws_missing_body()
                  : apiErrorMessage(view.error, m.ws_load_failed())}
              </p>
            </PanelBody>
          </>
        )}
      </Frame>
    );
  return (
    <WorkspaceHostContext.Provider value={host}>
      <CurrentWorkspaceContext.Provider value={{ slug, view: view.data }}>
        <Outlet />
      </CurrentWorkspaceContext.Provider>
    </WorkspaceHostContext.Provider>
  );
}

export function WorkspaceFrame() {
  const { view } = useWorkspace();
  return (
    // Keeps room for a list as Me's frame does, though no section puts one
    // beside a document yet: the sidebar is as wide here as around Me's pages
    // opened from it and in Me, so moving between them never changes its
    // width (the person, 2026-10-30).
    <Frame
      sidebar={<WorkspaceSidebar />}
      collapsed={<WorkspaceSidebarCollapsed />}
      label={view.name}
    >
      <Outlet />
    </Frame>
  );
}

/**
 * Me's page opened from a workspace's rows of my own things (me-place.ts):
 * that workspace's sidebar around it (Me's extension `useSidebar`), and Me
 * narrowed to its id while it is (set before the first paint, so the list
 * never shows unnarrowed first; dropped on leaving).
 */
export function useWorkspaceSidebarSwap(
  host: WorkspaceHost,
): MeSidebarSwap | null {
  const slug = useMePlace();
  const view = useQuery({
    ...workspaceViewQuery(useApi(), slug ?? ""),
    enabled: slug !== null,
  }).data;
  const [, setWorkspace] = useMeWorkspace();
  const workspace = slug !== null ? (view?.id ?? null) : null;
  useLayoutEffect(() => {
    if (!workspace) return;
    setWorkspace(workspace);
    return () => setWorkspace(null);
  }, [workspace, setWorkspace]);
  if (slug === null) return null;
  if (!view)
    return {
      sidebar: <EmptySidebar />,
      collapsed: <EmptySidebar />,
      label: "",
    };
  const inside = (node: ReactNode) => (
    <WorkspaceHostContext.Provider value={host}>
      <CurrentWorkspaceContext.Provider value={{ slug, view }}>
        {node}
      </CurrentWorkspaceContext.Provider>
    </WorkspaceHostContext.Provider>
  );
  return {
    sidebar: inside(<WorkspaceSidebar />),
    collapsed: inside(<WorkspaceSidebarCollapsed />),
    label: view.name,
  };
}

export function SettingsFrame() {
  const { slug, view } = useWorkspace();
  return (
    <Frame
      sidebar={<SettingsSidebar />}
      collapsed={<SettingsSidebar folded />}
      foldByWidthOnly
      label={m.ws_settings_of({ name: view.name })}
      list={false}
      menu="settings"
    >
      <SettingsDockUp index={`/${slug}/settings`} />
      <Outlet />
    </Frame>
  );
}

/**
 * /<slug> itself: sent on to where a section says the workspace opens (the
 * old web opened the spaces' home; now Topics, or the one space's overview).
 * With no such section, a line that says the screens come with the parts.
 */
export function WorkspaceHome() {
  const { slug, view } = useWorkspace();
  const host = useWorkspaceHost();
  const spaces = useWorkspaceSpaces();
  const to = host.sections
    .filter((s) => sectionShown(s, view, spaces))
    .map((s) => s.landing?.(spaces) ?? null)
    .find((p) => p !== null);
  if (to) return <Navigate to={`/${slug}${to}`} replace />;
  return (
    <>
      <PanelHeader title={view.name} />
      <PanelBody>
        <p className="max-w-[520px] text-[13px] leading-[1.7] text-faint">
          {m.ws_home_empty()}
        </p>
      </PanelBody>
    </>
  );
}
